import type { SoundBank } from './SoundBank';

/**
 * AmbientMixer — continuous looping ambient beds.
 *
 * Separate from SoundBank's one-shot model because ambient sounds need:
 *   - Continuous playback (not allocate-and-dispose per fire)
 *   - Seamless looping via crossfade of two AudioBufferSourceNodes (the
 *     boundary of a single looped buffer is almost always audible)
 *   - Smooth volume ramps when game state changes (time of day, weather,
 *     plot mode) — no popping in/out
 *   - Multiple beds layered simultaneously (forest + wind + crickets)
 *
 * Usage:
 *   const mixer = new AmbientMixer(bank);
 *   mixer.setBed('forest', 'environment/forest', 0.3);  // turn on
 *   mixer.setBed('forest', 'environment/forest', 0);    // fade out
 *   mixer.update(dt);                                    // call per frame
 *
 * Each registered bed has:
 *   - A persistent gain node connected to the SoundBank's master bus
 *   - One or two active AudioBufferSourceNodes (two during crossfade)
 *   - A target volume the current volume ramps toward
 *
 * The mixer doesn't know about game state directly — Sounds.ts calls
 * setBed() with target volumes derived from sim.getDate() etc.
 */
export class AmbientMixer {
  private ctx: AudioContext;
  private bank: SoundBank;
  private master: GainNode;
  private beds = new Map<string, Bed>();

  /** Crossfade duration when looping a single bed's source nodes. */
  private static readonly CROSSFADE_S = 2.0;
  /** How fast the current volume chases the target — units per second.
   *  0.5 means it takes ~2 seconds to go from 0 to 1.0. */
  private static readonly VOLUME_SMOOTH_PER_S = 0.5;

  constructor(bank: SoundBank) {
    this.bank = bank;
    this.ctx = bank.audioContext();
    this.master = bank.masterBus();
  }

  /** Register / update a named ambient slot. The mixer will fade
   *  toward `targetVolume` and (if non-zero) keep a source playing
   *  for `group`. Pass volume=0 to fade out (the source stops once
   *  fully silent). */
  setBed(name: string, group: string, targetVolume: number) {
    let bed = this.beds.get(name);
    if (!bed) {
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.master);
      bed = {
        group,
        targetVolume,
        currentVolume: 0,
        sources: [],
        gain,
      };
      this.beds.set(name, bed);
    } else {
      bed.targetVolume = targetVolume;
      // Allow swapping the bed's group on the fly (e.g.
      // forest-day → forest-night). The mixer will crossfade naturally
      // because the new source plays in alongside the old one as it ends.
      if (bed.group !== group) {
        bed.group = group;
        // Force-stop existing sources so the next ensureSource pulls
        // from the NEW group (crossfade across groups is overkill —
        // a sudden swap usually coincides with a multi-second volume
        // smooth so the ear doesn't notice anyway).
        for (const s of bed.sources) {
          try { s.src.stop(); } catch { /* already stopped */ }
        }
        bed.sources.length = 0;
      }
    }
  }

  /** Per-frame update — smooth volumes, spin up / crossfade sources. */
  update(dt: number) {
    const dvMax = AmbientMixer.VOLUME_SMOOTH_PER_S * dt;
    for (const bed of this.beds.values()) {
      // Smooth currentVolume toward targetVolume.
      const dv = bed.targetVolume - bed.currentVolume;
      if (Math.abs(dv) < 1e-4) {
        bed.currentVolume = bed.targetVolume;
      } else {
        bed.currentVolume += Math.sign(dv) * Math.min(Math.abs(dv), dvMax);
      }
      bed.gain.gain.value = bed.currentVolume;

      const wantPlaying = bed.currentVolume > 1e-3 || bed.targetVolume > 1e-3;
      if (wantPlaying) {
        this.ensureSource(bed);
      } else if (bed.sources.length > 0) {
        // Fully silent and we don't want it — stop active sources.
        for (const s of bed.sources) {
          try { s.src.stop(); } catch { /* already stopped */ }
        }
        bed.sources.length = 0;
      }
    }
  }

  /** Spin up a source if none is playing, or schedule a crossfade if
   *  the current source is nearing its end. */
  private ensureSource(bed: Bed) {
    const now = this.ctx.currentTime;
    // Drop sources that have already ended.
    bed.sources = bed.sources.filter(s => s.endsAt > now - 0.05);

    if (bed.sources.length === 0) {
      this.startSource(bed, now, 0);
      return;
    }

    // If exactly one source is playing and it's within CROSSFADE_S of
    // ending, kick off the next one with a fade-in and a corresponding
    // fade-out on the outgoing source.
    if (bed.sources.length === 1) {
      const last = bed.sources[0];
      const remaining = last.endsAt - now;
      if (remaining < AmbientMixer.CROSSFADE_S) {
        this.startSource(bed, now, AmbientMixer.CROSSFADE_S);
        // Fade the outgoing source down across the crossfade window.
        last.fadeGain.gain.cancelScheduledValues(now);
        last.fadeGain.gain.setValueAtTime(last.fadeGain.gain.value, now);
        last.fadeGain.gain.linearRampToValueAtTime(0, now + remaining);
      }
    }
  }

  /** Allocate a new AudioBufferSourceNode for the bed and wire it
   *  through a fade-in gain into the bed's main gain. */
  private startSource(bed: Bed, startTime: number, fadeInS: number) {
    const entry = this.bank.randomBuffer(bed.group);
    if (!entry) return;     // group not loaded yet — try again next tick

    const src = this.ctx.createBufferSource();
    src.buffer = entry.buf;
    // We handle our own looping via overlap-crossfade rather than
    // letting Web Audio loop the buffer internally — that would
    // produce an audible seam every loop, which is exactly what we're
    // trying to avoid.
    src.loop = false;

    const fadeGain = this.ctx.createGain();
    // Bake the per-clip normalization gain into the fade gain so
    // ambient beds match loudness regardless of source file level.
    const baseGain = entry.gain;
    if (fadeInS > 0) {
      fadeGain.gain.setValueAtTime(0, startTime);
      fadeGain.gain.linearRampToValueAtTime(baseGain, startTime + fadeInS);
    } else {
      fadeGain.gain.setValueAtTime(baseGain, startTime);
    }

    src.connect(fadeGain).connect(bed.gain);
    src.start(startTime);
    bed.sources.push({
      src,
      fadeGain,
      startedAt: startTime,
      endsAt: startTime + entry.buf.duration,
    });
  }

  /** Stop everything and clear state. Used on dispose / reset. */
  stopAll() {
    for (const bed of this.beds.values()) {
      for (const s of bed.sources) {
        try { s.src.stop(); } catch { /* already stopped */ }
      }
      bed.sources.length = 0;
      bed.gain.gain.value = 0;
      bed.currentVolume = 0;
      bed.targetVolume = 0;
    }
  }
}

interface Bed {
  group: string;
  targetVolume: number;
  currentVolume: number;
  sources: AmbientSource[];
  gain: GainNode;
}

interface AmbientSource {
  src: AudioBufferSourceNode;
  fadeGain: GainNode;
  startedAt: number;
  endsAt: number;
}
